import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type { PowerSelectGroupSignature } from 'ember-power-select/components/power-select/power-select-group';
import type { Select } from 'ember-power-select/components/power-select-multiple';
import { emberPowerSelectIsSelected } from 'ember-power-select/helpers/ember-power-select-is-selected';

import type {
  Group,
  Option,
} from '../../../../types/components/prism/combo-select-input.ts';
import PrismCheckbox from '../../checkbox.gts';
import PrismIcon from '../../icon.gts';

interface PrismComboSelectInputUnsafeMultipleSelectionGroupSignature extends PowerSelectGroupSignature<
  Option,
  Group<Option>
> {}

export default class PrismComboSelectInputUnsafeMultipleSelectionGroup extends Component<PrismComboSelectInputUnsafeMultipleSelectionGroupSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCollapsed() {
    return (
      'unsafe_isCollapsed' in this.args.group &&
      this.args.group.unsafe_isCollapsed
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCollapsible() {
    return 'unsafe_isCollapsed' in this.args.group;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isGroupPartiallySelected() {
    return this.nonDisabledGroupOptions.some((option) =>
      emberPowerSelectIsSelected([option, this.select.selected]),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isGroupSelected() {
    return this.nonDisabledGroupOptions.every((option) =>
      emberPowerSelectIsSelected([option, this.select.selected]),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nonDisabledGroupOptions() {
    return this.args.group.options.filter(
      (option) => !('disabled' in option && option.disabled),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get select() {
    return this.args.select as Select<Option, Group<Option>>;
  }

  @action
  handleGroupClick(): void {
    const options = new Set(this.select.selected);

    if (!this.isGroupSelected) {
      // Make sure group options only get added once if some are already selected
      this.nonDisabledGroupOptions.forEach((option) =>
        options.add(option as Option),
      );
      this.select.actions.select(Array.from(options));
      return;
    }

    // Delete only the specific group options that are selected
    this.nonDisabledGroupOptions.forEach((option) =>
      options.delete(option as Option),
    );
    this.select.actions.select(Array.from(options));
  }

  @action
  handleGroupExpandClick(): void {
    this.select.actions.choose(this.args.group as unknown as Option[]);
  }

  @action
  handleGroupMouseEnter(): void {
    // Makes sure no other options are highlighted by power-select's internal API
    this.select.actions.highlight(null);
  }

  <template>
    {{! template-lint-disable no-nested-interactive require-mandatory-role-attributes require-presentational-children }}
    {{#if this.isCollapsible}}
      {{#if this.isCollapsed}}
        {{! this component isn't aria-compliant }}
        <li
          class="unsafe-collapsible-group"
          role="option"
          {{on "click" this.handleGroupExpandClick}}
          {{on "mouseenter" this.handleGroupMouseEnter}}
        >
          <p class="text-300--regular unsafe-collapsible-group__label truncate">
            {{@group.groupName}}
          </p>
          <PrismIcon @name="chevron-right" @size="small" />
        </li>
      {{else}}
        {{yield}}
      {{/if}}
    {{else}}
      <li class="multiple-selection-group" role="presentation">
        <div
          aria-selected={{if this.isGroupSelected "true"}}
          class="multiple-selection-group__option"
          role="option"
          {{on "click" this.handleGroupClick}}
          {{on "mouseenter" this.handleGroupMouseEnter}}
        >
          <div class="multiple-selection-group__checkbox-container">
            <PrismCheckbox
              @checked={{this.isGroupSelected}}
              @indeterminate={{this.isGroupPartiallySelected}}
              tabindex="-1"
            />
          </div>
          <p class="text-300--regular truncate">{{@group.groupName}}</p>
        </div>
        {{yield}}
      </li>
    {{/if}}
  </template>
}
